/**
 * Workflow Designer
 * 
 * Visual step-by-step workflow builder with:
 * - Drag & drop step reordering
 * - Domain/tool selection
 * - Condition builder
 * - Enforcement configuration
 * - Data flow mapping
 */

import React, { useState, useEffect, useCallback } from 'react';

interface DomainInfo {
  id: string;
  name: string;
  icon: string;
  toolCount: number;
}

interface ToolInfo {
  name: string;
  description: string;
}

interface WorkflowStep {
  id?: number;
  stepOrder: number;
  domainId: string;
  toolName: string;
  description?: string;
  expectedOutput?: string;
  inputHint?: string;
  condition?: string;
  required: boolean;
  skipable: boolean;
  mustSucceed: boolean;
  onFailure: 'halt' | 'warn' | 'continue';
  alwaysRun: boolean;
}

interface WorkflowDataFlow {
  id?: number;
  sourceDomain?: string;
  sourceOutput?: string;
  targetDomain?: string;
  targetInput?: string;
  description?: string;
}

interface Workflow {
  id?: number;
  toolServerId?: number;
  workflowId: string;
  name: string;
  description?: string;
  triggerDescription?: string;
  enforcement: 'strict' | 'recommended' | 'flexible';
  isActive: boolean;
  steps: WorkflowStep[];
  dataFlows: WorkflowDataFlow[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface OrganizationAccess {
  id: number;
  name: string;
  is_enabled: boolean;
  granted_by?: number;
  granted_at?: string;
}

interface WorkflowDesignerProps {
  workflow?: Workflow;
  serverId: number;
  domains: DomainInfo[];
  onSave: (workflow: Workflow) => void;
  onCancel: () => void;
  fetchDomainTools: (domainId: string) => void;
  getDomainTools: (domainId: string) => ToolInfo[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const WorkflowDesigner: React.FC<WorkflowDesignerProps> = ({
  workflow,
  serverId,
  domains,
  onSave,
  onCancel,
  fetchDomainTools,
  getDomainTools
}) => {
  // Form state
  const [workflowId, setWorkflowId] = useState(workflow?.workflowId || '');
  const [name, setName] = useState(workflow?.name || '');
  const [description, setDescription] = useState(workflow?.description || '');
  const [triggerDescription, setTriggerDescription] = useState(workflow?.triggerDescription || '');
  const [enforcement, setEnforcement] = useState<'strict' | 'recommended' | 'flexible'>(
    workflow?.enforcement || 'flexible'
  );
  const [isActive, setIsActive] = useState(workflow?.isActive ?? true);
  const [steps, setSteps] = useState<WorkflowStep[]>(workflow?.steps || []);
  const [dataFlows, setDataFlows] = useState<WorkflowDataFlow[]>(workflow?.dataFlows || []);
  
  // UI state
  const [activeTab, setActiveTab] = useState<'info' | 'steps' | 'dataflow' | 'access'>('info');
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [draggedStep, setDraggedStep] = useState<number | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Organization access state
  const [organizations, setOrganizations] = useState<OrganizationAccess[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [restrictToOrg, setRestrictToOrg] = useState<number | null>(null);

  // Fetch tools for domains used in steps
  useEffect(() => {
    const uniqueDomains = new Set(steps.map(s => s.domainId).filter(Boolean));
    uniqueDomains.forEach(domainId => {
      fetchDomainTools(domainId);
    });
  }, [steps, fetchDomainTools]);

  // Fetch organization access when switching to access tab
  const fetchOrganizations = useCallback(async () => {
    if (!workflow?.id) return;
    
    setLoadingOrgs(true);
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${serverId}/workflows/${workflow.id}/organizations`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch organizations');
      
      const data = await response.json();
      if (data.success) {
        setOrganizations(data.organizations);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  }, [workflow?.id, serverId]);

  // Fetch orgs when access tab is selected
  useEffect(() => {
    if (activeTab === 'access' && workflow?.id) {
      fetchOrganizations();
    }
  }, [activeTab, workflow?.id, fetchOrganizations]);

  // Toggle organization access
  const handleToggleOrgAccess = async (orgId: number, currentlyEnabled: boolean) => {
    if (!workflow?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${serverId}/workflows/${workflow.id}/organizations/${orgId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isEnabled: !currentlyEnabled })
        }
      );
      
      if (!response.ok) throw new Error('Failed to update access');
      
      // Update local state
      setOrganizations(prev => 
        prev.map(org => 
          org.id === orgId ? { ...org, is_enabled: !currentlyEnabled } : org
        )
      );
    } catch (err) {
      console.error('Error toggling access:', err);
    }
  };

  // Restrict to single organization
  const handleRestrictToOrg = async (orgId: number) => {
    if (!workflow?.id) return;
    
    if (!window.confirm(`This will restrict this workflow to ONLY the selected organization. All other organizations will lose access. Continue?`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${serverId}/workflows/${workflow.id}/organizations/restrict`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ orgId })
        }
      );
      
      if (!response.ok) throw new Error('Failed to restrict workflow');
      
      // Refresh organization list
      fetchOrganizations();
      setRestrictToOrg(null);
    } catch (err) {
      console.error('Error restricting workflow:', err);
    }
  };

  // Filter organizations by search term
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
  );

  // Auto-generate workflow ID from name
  useEffect(() => {
    if (!workflow?.id && name && !workflowId) {
      const generated = name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      setWorkflowId(generated);
    }
  }, [name, workflowId, workflow?.id]);

  // Validation
  const validate = useCallback((): boolean => {
    const errors: string[] = [];
    
    if (!workflowId.trim()) errors.push('Workflow ID is required');
    if (!name.trim()) errors.push('Workflow name is required');
    if (steps.length === 0) errors.push('At least one step is required');
    
    // Validate steps
    steps.forEach((step, idx) => {
      if (!step.domainId) errors.push(`Step ${idx + 1}: Domain is required`);
      if (!step.toolName) errors.push(`Step ${idx + 1}: Tool is required`);
    });
    
    // Validate workflow ID format
    if (!/^[a-z0-9-]+$/.test(workflowId)) {
      errors.push('Workflow ID must contain only lowercase letters, numbers, and hyphens');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  }, [workflowId, name, steps]);

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;
    
    setSaving(true);
    
    const workflowData: Workflow = {
      id: workflow?.id,
      toolServerId: serverId,
      workflowId,
      name,
      description,
      triggerDescription,
      enforcement,
      isActive,
      steps: steps.map((s, idx) => ({ ...s, stepOrder: idx + 1 })),
      dataFlows
    };
    
    await onSave(workflowData);
    setSaving(false);
  };

  // Step management
  const addStep = () => {
    const newStep: WorkflowStep = {
      stepOrder: steps.length + 1,
      domainId: '',
      toolName: '',
      description: '',
      expectedOutput: '',
      inputHint: '',
      condition: '',
      required: false,
      skipable: true,
      mustSucceed: false,
      onFailure: 'continue',
      alwaysRun: false
    };
    setSteps([...steps, newStep]);
    setEditingStep(steps.length);
    setExpandedSteps(prev => new Set(prev).add(steps.length));
  };

  const updateStep = (index: number, updates: Partial<WorkflowStep>) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], ...updates };
    setSteps(newSteps);
    
    // Fetch tools if domain changed
    if (updates.domainId && updates.domainId !== steps[index].domainId) {
      fetchDomainTools(updates.domainId);
    }
  };

  const deleteStep = (index: number) => {
    if (window.confirm('Are you sure you want to delete this step?')) {
      const newSteps = steps.filter((_, i) => i !== index);
      setSteps(newSteps);
      setEditingStep(null);
    }
  };

  const duplicateStep = (index: number) => {
    const newSteps = [...steps];
    const duplicate = { ...steps[index], id: undefined };
    newSteps.splice(index + 1, 0, duplicate);
    setSteps(newSteps);
  };

  // Drag & drop
  const handleDragStart = (index: number) => {
    setDraggedStep(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedStep === null || draggedStep === index) return;
    
    const newSteps = [...steps];
    const draggedItem = newSteps[draggedStep];
    newSteps.splice(draggedStep, 1);
    newSteps.splice(index, 0, draggedItem);
    setSteps(newSteps);
    setDraggedStep(index);
  };

  const handleDragEnd = () => {
    setDraggedStep(null);
  };

  // Toggle step expansion
  const toggleStepExpansion = (index: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Data flow management
  const addDataFlow = () => {
    setDataFlows([...dataFlows, {
      sourceDomain: '',
      sourceOutput: '',
      targetDomain: '',
      targetInput: '',
      description: ''
    }]);
  };

  const updateDataFlow = (index: number, updates: Partial<WorkflowDataFlow>) => {
    const newFlows = [...dataFlows];
    newFlows[index] = { ...newFlows[index], ...updates };
    setDataFlows(newFlows);
  };

  const deleteDataFlow = (index: number) => {
    setDataFlows(dataFlows.filter((_, i) => i !== index));
  };

  // Get domain icon
  const getDomainIcon = (domainId: string) => {
    const domain = domains.find(d => d.id === domainId);
    return domain?.icon || '🔧';
  };

  // Get unique domains used in steps
  const getUsedDomains = (): string[] => {
    const domainIds = steps.map(s => s.domainId).filter(Boolean);
    return Array.from(new Set(domainIds));
  };

  return (
    <div className="workflow-designer">
      {/* Header */}
      <div className="designer-header">
        <div className="header-left">
          <button className="back-btn" onClick={onCancel}>
            ← Back
          </button>
          <h2>{workflow?.id ? 'Edit Workflow' : 'Create New Workflow'}</h2>
        </div>
        <div className="header-right">
          <button className="btn secondary" onClick={onCancel}>Cancel</button>
          <button 
            className="btn primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : (workflow?.id ? 'Save Changes' : 'Create Workflow')}
          </button>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <strong>⚠️ Please fix the following errors:</strong>
          <ul>
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabs */}
      <div className="designer-tabs">
        <button 
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          📝 Basic Info
        </button>
        <button 
          className={`tab ${activeTab === 'steps' ? 'active' : ''}`}
          onClick={() => setActiveTab('steps')}
        >
          📋 Steps ({steps.length})
        </button>
        <button 
          className={`tab ${activeTab === 'dataflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('dataflow')}
        >
          🔗 Data Flow ({dataFlows.length})
        </button>
        {workflow?.id && (
          <button 
            className={`tab ${activeTab === 'access' ? 'active' : ''}`}
            onClick={() => setActiveTab('access')}
          >
            🏢 Org Access
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="designer-content">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-tab">
            <div className="form-grid">
              <div className="form-group">
                <label>Workflow ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={workflowId}
                  onChange={e => setWorkflowId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="bug-tracking"
                  disabled={!!workflow?.id}
                />
                <span className="hint">Unique identifier (lowercase, no spaces)</span>
              </div>
              
              <div className="form-group">
                <label>Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Bug Report & Tracking"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what this workflow does..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>🎯 Trigger Description</label>
              <textarea
                value={triggerDescription}
                onChange={e => setTriggerDescription(e.target.value)}
                placeholder="When should the AI use this workflow? E.g., 'When user reports a bug or issue'"
                rows={2}
              />
              <span className="hint">Tells the AI when to use this workflow</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Enforcement Level</label>
                <div className="enforcement-selector">
                  <button
                    className={`enforcement-btn ${enforcement === 'strict' ? 'active' : ''}`}
                    onClick={() => setEnforcement('strict')}
                  >
                    <span className="icon">🔒</span>
                    <span className="label">Strict</span>
                    <span className="desc">AI must follow exactly</span>
                  </button>
                  <button
                    className={`enforcement-btn ${enforcement === 'recommended' ? 'active' : ''}`}
                    onClick={() => setEnforcement('recommended')}
                  >
                    <span className="icon">📌</span>
                    <span className="label">Recommended</span>
                    <span className="desc">AI should follow</span>
                  </button>
                  <button
                    className={`enforcement-btn ${enforcement === 'flexible' ? 'active' : ''}`}
                    onClick={() => setEnforcement('flexible')}
                  >
                    <span className="icon">💡</span>
                    <span className="label">Flexible</span>
                    <span className="desc">AI uses as guidance</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                <span>Active</span>
                <span className="hint">(Inactive workflows won't be presented to the AI)</span>
              </label>
            </div>
          </div>
        )}

        {/* Steps Tab */}
        {activeTab === 'steps' && (
          <div className="steps-tab">
            <div className="steps-header">
              <h3>Workflow Steps</h3>
              <button className="btn secondary" onClick={addStep}>
                ➕ Add Step
              </button>
            </div>

            {steps.length === 0 ? (
              <div className="empty-steps">
                <div className="empty-icon">📋</div>
                <h4>No Steps Yet</h4>
                <p>Add steps to define the workflow procedure.</p>
                <button className="btn primary" onClick={addStep}>
                  ➕ Add First Step
                </button>
              </div>
            ) : (
              <div className="steps-list">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`step-card ${draggedStep === index ? 'dragging' : ''} ${expandedSteps.has(index) ? 'expanded' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Step Header */}
                    <div className="step-header" onClick={() => toggleStepExpansion(index)}>
                      <div className="step-drag-handle">⋮⋮</div>
                      <div className="step-number">{index + 1}</div>
                      <div className="step-summary">
                        <span className="step-domain-icon">{getDomainIcon(step.domainId)}</span>
                        <span className="step-tool-name">{step.toolName || 'Select tool...'}</span>
                        <span className="step-domain-name">({step.domainId || 'no domain'})</span>
                        {step.required && <span className="step-badge required">Required</span>}
                        {step.condition && <span className="step-badge condition">Conditional</span>}
                      </div>
                      <div className="step-actions-mini">
                        <button onClick={(e) => { e.stopPropagation(); duplicateStep(index); }}>📋</button>
                        <button onClick={(e) => { e.stopPropagation(); deleteStep(index); }}>🗑️</button>
                        <span className="expand-icon">{expandedSteps.has(index) ? '▼' : '▶'}</span>
                      </div>
                    </div>

                    {/* Step Details (Expanded) */}
                    {expandedSteps.has(index) && (
                      <div className="step-details">
                        <div className="step-form-grid">
                          {/* Domain & Tool Selection */}
                          <div className="form-group">
                            <label>Domain</label>
                            <select
                              value={step.domainId}
                              onChange={e => updateStep(index, { domainId: e.target.value, toolName: '' })}
                            >
                              <option value="">Select domain...</option>
                              {domains.map(d => (
                                <option key={d.id} value={d.id}>
                                  {d.icon} {d.name} ({d.toolCount} tools)
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Tool</label>
                            <select
                              value={step.toolName}
                              onChange={e => updateStep(index, { toolName: e.target.value })}
                              disabled={!step.domainId}
                            >
                              <option value="">Select tool...</option>
                              {getDomainTools(step.domainId).map(t => (
                                <option key={t.name} value={t.name}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Description</label>
                          <input
                            type="text"
                            value={step.description || ''}
                            onChange={e => updateStep(index, { description: e.target.value })}
                            placeholder="What does this step do?"
                          />
                        </div>

                        <div className="form-group">
                          <label>Condition (Optional)</label>
                          <input
                            type="text"
                            value={step.condition || ''}
                            onChange={e => updateStep(index, { condition: e.target.value })}
                            placeholder="e.g., If no existing ticket found"
                          />
                          <span className="hint">When should this step execute?</span>
                        </div>

                        <div className="step-form-grid">
                          <div className="form-group">
                            <label>Expected Output</label>
                            <input
                              type="text"
                              value={step.expectedOutput || ''}
                              onChange={e => updateStep(index, { expectedOutput: e.target.value })}
                              placeholder="What to expect from this step"
                            />
                          </div>

                          <div className="form-group">
                            <label>Input Hint</label>
                            <input
                              type="text"
                              value={step.inputHint || ''}
                              onChange={e => updateStep(index, { inputHint: e.target.value })}
                              placeholder="How to use previous outputs"
                            />
                          </div>
                        </div>

                        {/* Enforcement Options */}
                        <div className="step-enforcement">
                          <h4>Step Enforcement</h4>
                          <div className="enforcement-options">
                            <label className="checkbox-option">
                              <input
                                type="checkbox"
                                checked={step.required}
                                onChange={e => updateStep(index, { required: e.target.checked })}
                              />
                              <span>Required</span>
                              <span className="hint">Cannot be skipped</span>
                            </label>

                            <label className="checkbox-option">
                              <input
                                type="checkbox"
                                checked={!step.skipable}
                                onChange={e => updateStep(index, { skipable: !e.target.checked })}
                              />
                              <span>Not Skipable</span>
                              <span className="hint">Even with user permission</span>
                            </label>

                            <label className="checkbox-option">
                              <input
                                type="checkbox"
                                checked={step.mustSucceed}
                                onChange={e => updateStep(index, { mustSucceed: e.target.checked })}
                              />
                              <span>Must Succeed</span>
                              <span className="hint">Next step requires success</span>
                            </label>

                            <label className="checkbox-option">
                              <input
                                type="checkbox"
                                checked={step.alwaysRun}
                                onChange={e => updateStep(index, { alwaysRun: e.target.checked })}
                              />
                              <span>Always Run</span>
                              <span className="hint">Even if previous steps failed</span>
                            </label>
                          </div>

                          {step.mustSucceed && (
                            <div className="form-group on-failure">
                              <label>On Failure:</label>
                              <div className="radio-group">
                                <label>
                                  <input
                                    type="radio"
                                    name={`onFailure-${index}`}
                                    checked={step.onFailure === 'halt'}
                                    onChange={() => updateStep(index, { onFailure: 'halt' })}
                                  />
                                  🛑 Halt Workflow
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`onFailure-${index}`}
                                    checked={step.onFailure === 'warn'}
                                    onChange={() => updateStep(index, { onFailure: 'warn' })}
                                  />
                                  ⚠️ Warn & Continue
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`onFailure-${index}`}
                                    checked={step.onFailure === 'continue'}
                                    onChange={() => updateStep(index, { onFailure: 'continue' })}
                                  />
                                  ➡️ Continue Silently
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Connection line to next step */}
                    {index < steps.length - 1 && (
                      <div className="step-connector">
                        <div className="connector-line"></div>
                        <div className="connector-arrow">▼</div>
                      </div>
                    )}
                  </div>
                ))}

                <button className="add-step-btn" onClick={addStep}>
                  <span className="plus">+</span>
                  Add Another Step
                </button>
              </div>
            )}
          </div>
        )}

        {/* Data Flow Tab */}
        {activeTab === 'dataflow' && (
          <div className="dataflow-tab">
            <div className="dataflow-header">
              <div>
                <h3>Data Flow Mappings</h3>
                <p className="hint">Define how data flows between steps and domains</p>
              </div>
              <button className="btn secondary" onClick={addDataFlow}>
                ➕ Add Data Flow
              </button>
            </div>

            {dataFlows.length === 0 ? (
              <div className="empty-dataflow">
                <div className="empty-icon">🔗</div>
                <h4>No Data Flows Defined</h4>
                <p>Data flows help the AI understand how to pass data between steps.</p>
                <button className="btn primary" onClick={addDataFlow}>
                  ➕ Add Data Flow
                </button>
              </div>
            ) : (
              <div className="dataflow-list">
                {dataFlows.map((flow, index) => (
                  <div key={index} className="dataflow-card">
                    <div className="dataflow-visual">
                      <div className="flow-source">
                        <select
                          value={flow.sourceDomain || ''}
                          onChange={e => updateDataFlow(index, { sourceDomain: e.target.value })}
                        >
                          <option value="">Source Domain...</option>
                          {getUsedDomains().map(d => (
                            <option key={d} value={d}>{getDomainIcon(d)} {d}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={flow.sourceOutput || ''}
                          onChange={e => updateDataFlow(index, { sourceOutput: e.target.value })}
                          placeholder="output field"
                        />
                      </div>
                      
                      <div className="flow-arrow">
                        <span>→</span>
                      </div>
                      
                      <div className="flow-target">
                        <select
                          value={flow.targetDomain || ''}
                          onChange={e => updateDataFlow(index, { targetDomain: e.target.value })}
                        >
                          <option value="">Target Domain...</option>
                          {getUsedDomains().map(d => (
                            <option key={d} value={d}>{getDomainIcon(d)} {d}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={flow.targetInput || ''}
                          onChange={e => updateDataFlow(index, { targetInput: e.target.value })}
                          placeholder="input field"
                        />
                      </div>
                      
                      <button 
                        className="delete-flow-btn"
                        onClick={() => deleteDataFlow(index)}
                      >
                        🗑️
                      </button>
                    </div>
                    
                    <input
                      type="text"
                      className="flow-description"
                      value={flow.description || ''}
                      onChange={e => updateDataFlow(index, { description: e.target.value })}
                      placeholder="Description (optional)"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Visual Preview */}
            {steps.length > 0 && (
              <div className="workflow-preview">
                <h4>📊 Workflow Preview</h4>
                <div className="preview-diagram">
                  {steps.map((step, idx) => (
                    <React.Fragment key={idx}>
                      <div className="preview-step">
                        <div className="preview-step-icon">{getDomainIcon(step.domainId)}</div>
                        <div className="preview-step-info">
                          <strong>{step.toolName || 'Step ' + (idx + 1)}</strong>
                          <span>{step.domainId}</span>
                        </div>
                      </div>
                      {idx < steps.length - 1 && (
                        <div className="preview-connector">
                          {dataFlows.some(f => 
                            f.sourceDomain === step.domainId && 
                            steps[idx + 1] && f.targetDomain === steps[idx + 1].domainId
                          ) ? '⟶' : '→'}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Organization Access Tab */}
        {activeTab === 'access' && (
          <div className="access-tab">
            <div className="access-header">
              <div>
                <h3>🏢 Organization Access</h3>
                <p className="hint">Control which organizations can use this workflow</p>
              </div>
            </div>

            {!workflow?.id ? (
              <div className="empty-access">
                <div className="empty-icon">💾</div>
                <h4>Save Workflow First</h4>
                <p>You need to save the workflow before managing organization access.</p>
              </div>
            ) : loadingOrgs ? (
              <div className="loading-orgs">
                <div className="spinner"></div>
                <p>Loading organizations...</p>
              </div>
            ) : (
              <>
                {/* Quick Actions */}
                <div className="access-actions">
                  <div className="search-box">
                    <input
                      type="text"
                      value={orgSearchTerm}
                      onChange={e => setOrgSearchTerm(e.target.value)}
                      placeholder="🔍 Search organizations..."
                    />
                  </div>
                  
                  <div className="restrict-section">
                    <select 
                      value={restrictToOrg || ''} 
                      onChange={e => setRestrictToOrg(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">🔒 Restrict to single org...</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    {restrictToOrg && (
                      <button 
                        className="btn danger"
                        onClick={() => handleRestrictToOrg(restrictToOrg)}
                      >
                        Restrict
                      </button>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="access-stats">
                  <div className="stat">
                    <span className="stat-value">{organizations.filter(o => o.is_enabled).length}</span>
                    <span className="stat-label">With Access</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{organizations.filter(o => !o.is_enabled).length}</span>
                    <span className="stat-label">Excluded</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{organizations.length}</span>
                    <span className="stat-label">Total Orgs</span>
                  </div>
                </div>

                {/* Organization List */}
                <div className="org-list">
                  {filteredOrgs.length === 0 ? (
                    <p className="no-orgs">No organizations found</p>
                  ) : (
                    filteredOrgs.map(org => (
                      <div 
                        key={org.id} 
                        className={`org-item ${org.is_enabled ? 'enabled' : 'disabled'}`}
                      >
                        <div className="org-info">
                          <span className="org-name">{org.name}</span>
                          <span className="org-id">ID: {org.id}</span>
                        </div>
                        <button
                          className={`toggle-btn ${org.is_enabled ? 'on' : 'off'}`}
                          onClick={() => handleToggleOrgAccess(org.id, org.is_enabled)}
                          title={org.is_enabled ? 'Click to revoke access' : 'Click to grant access'}
                        >
                          {org.is_enabled ? '✅ Has Access' : '❌ No Access'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowDesigner;

